// Units of measure for asset quantities. Defined per asset type in the
// Citycare metadata standards (File 2, column R) per PRD §3 Terminology and
// §7.4.1. Capture UI adapts input controls based on this value:
//   - COUNT: integer stepper with +/- buttons.
//   - METRE / SQUARE_METRE: decimal input with numeric keypad.
//   - CUBIC_METRE: legacy-only — no longer user-selectable but still
//     renders correctly if seed/legacy data exists (see ADR below).
//
// `'m3'` (CUBIC_METRE) is retained in the type, constant, and DB enum
// so any historical asset_type rows with `unit_of_measure = 'm3'` still
// render through capture/breakdown/export paths without errors. It is
// excluded from `SELECTABLE_UNIT_OF_MEASURE_VALUES` so neither admins
// nor field staff can choose it in new forms. To fully drop the value,
// a future migration would need to (a) verify zero rows reference 'm3'
// and (b) ALTER the postgres enum — neither is in scope here.

import { unitOfMeasureEnum } from '../db/schema'

export const UNIT_OF_MEASURE = {
  COUNT: 'Count',
  METRE: 'm',
  SQUARE_METRE: 'm2',
  CUBIC_METRE: 'm3',
} as const satisfies Record<
  string,
  'Count' | 'm' | 'm2' | 'm3'
>

export type UnitOfMeasure = 'Count' | 'm' | 'm2' | 'm3'

/**
 * All UoM enum values, including legacy `'m3'`. Use this for type-
 * derivation, schema validation, and any path that must render existing
 * rows (capture inputs, breakdown labels, CSV export).
 */
export const UNIT_OF_MEASURE_VALUES = unitOfMeasureEnum.enumValues

/**
 * UoM values offered in user-facing dropdowns (admin asset-type
 * create/edit forms; field-staff variant creation). `'m3'` is omitted
 * because product owner removed it as a supported new-asset option;
 * see header comment.
 *
 * Typed as a non-empty readonly tuple so callers can use the first
 * element as a safe default without unwrapping `undefined`.
 */
export const SELECTABLE_UNIT_OF_MEASURE_VALUES: readonly [
  Exclude<UnitOfMeasure, 'm3'>,
  ...Exclude<UnitOfMeasure, 'm3'>[],
] = ['Count', 'm', 'm2'] as const
